import { Loader2, TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Timeline, type ViewState } from "~/components/timeline";
import { useChunkedCompoundTimelineLoader } from "~/features/compoundEntities/hooks";
import type { CompoundSearchCondition } from "~/features/compoundEntities/types";
import { buildCompoundWhere } from "~/features/compoundEntities/utils/transformers";
import { useTenant } from "~/features/tenant/context";
import { parseGdtfRange } from "~/utils/gdtfRange";

/** Max years the timeline can render at minimum zoom (~800px / 0.1 scale). */
const TIMELINE_MAX_YEAR_RANGE = 8000;

interface CompoundEntitiesTimelineViewProps {
  conditions: readonly CompoundSearchCondition[];
  version?: string;
  gdtfFrom?: string;
  gdtfTo?: string;
  onSelect: (id: string) => void;
  initialViewState?: ViewState;
  onViewStateChange?: (centerYear: number, scale: number) => void;
}

export function CompoundEntitiesTimelineView({
  conditions,
  version,
  gdtfFrom,
  gdtfTo,
  onSelect,
  initialViewState,
  onViewStateChange,
}: CompoundEntitiesTimelineViewProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");

  const gdtfParsed = useMemo(() => parseGdtfRange(gdtfFrom, gdtfTo), [gdtfFrom, gdtfTo]);

  const gdtfRangeExceedsCapacity = useMemo(() => {
    if (!gdtfParsed.hasFilter || gdtfParsed.inverted) return false;
    if (gdtfParsed.fromYear == null || gdtfParsed.toYear == null) return false;
    return Math.abs(gdtfParsed.toYear - gdtfParsed.fromYear) > TIMELINE_MAX_YEAR_RANGE;
  }, [gdtfParsed]);

  // The chunked loader adds per-chunk dateJdn bounds on top of this base filter.
  const baseWhere = useMemo(
    () => buildCompoundWhere(conditions, version, gdtfFrom, gdtfTo),
    [conditions, version, gdtfFrom, gdtfTo],
  );
  const filterHash = useMemo(() => JSON.stringify(baseWhere), [baseWhere]);

  const { entities, loading, onVisibleYearRangeChange } = useChunkedCompoundTimelineLoader({
    tenantId,
    baseWhere,
    filterHash,
    orderBy: [{ date: "ASC" }],
    gdtfFrom,
    gdtfTo,
  });

  // When a GDTF date filter is active, center the timeline on the filtered range.
  const gdtfViewState = useMemo<ViewState | undefined>(() => {
    if (!gdtfParsed.hasFilter || gdtfParsed.inverted) return undefined;
    const { fromYear, toYear } = gdtfParsed;
    if (fromYear == null && toYear == null) return undefined;
    const lo = fromYear ?? (toYear as number) - 500;
    const hi = toYear ?? (fromYear as number) + 500;
    const center = (lo + hi) / 2;
    const rangeYears = Math.max(1, hi - lo);
    const scale = Math.max(0.1, 640 / rangeYears);
    return { offsetX: 400 - center * scale, offsetY: 0, scale };
  }, [gdtfParsed]);

  const timelineKey = gdtfViewState ? filterHash : "default";

  return (
    <div className="space-y-2">
      {gdtfRangeExceedsCapacity && (
        <p className="flex items-center gap-2 rounded-[var(--radius-sm)] bg-warning-bg px-3 py-2 text-12-regular text-fg-1">
          <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden />
          {t("ui.gdtfTimelineCapacityWarning", { maxYears: TIMELINE_MAX_YEAR_RANGE })}
        </p>
      )}
      <div className="relative rounded-[var(--radius-md)] border border-border-default bg-white">
        {loading && (
          <Loader2 className="absolute right-2 top-2 z-10 h-4 w-4 animate-spin text-fg-3" aria-label="loading" />
        )}
        <Timeline
          key={timelineKey}
          entities={entities}
          height={600}
          storageKey="v2-compound-entities-list"
          onEntityClick={(entity) => onSelect(entity.id)}
          onVisibleYearRangeChange={onVisibleYearRangeChange}
          initialViewState={gdtfViewState ?? initialViewState}
          onViewStateChange={onViewStateChange}
        />
      </div>
    </div>
  );
}
